<template>
  <div class="school-map-page">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>学校地图看板</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="top-bar">
      <div class="page-intro">
        <h2>学校地图看板</h2>
        <p>汇总 VIP、VIP2 以及校长用户的学校位置，支持查看具体经纬度与地址。</p>
      </div>
      <el-button size="small" type="primary" icon="el-icon-refresh" :loading="loading" @click="initDashboard">
        刷新数据
      </el-button>
    </div>

    <div class="summary-grid">
      <div class="summary-card">
        <div class="summary-label">学校总数</div>
        <div class="summary-value">{{ summary.total }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">已定位学校</div>
        <div class="summary-value">{{ summary.located }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">VIP 学校</div>
        <div class="summary-value">{{ summary.vip }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">VIP 校长</div>
        <div class="summary-value">{{ summary.vipPrincipal }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">VIP2 学校</div>
        <div class="summary-value">{{ summary.vip2 }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">VIP2 校长</div>
        <div class="summary-value">{{ summary.vip2Principal }}</div>
      </div>
    </div>

    <div class="panel filters-panel">
      <el-form :inline="true" size="small">
        <el-form-item label="关键词">
          <el-input
            v-model.trim="keyword"
            clearable
            placeholder="校区名 / 手机号 / 地址"
            @input="handleFilterChange">
          </el-input>
        </el-form-item>
        <el-form-item label="数据来源">
          <el-select v-model="sourceFilter" placeholder="全部来源" @change="handleFilterChange">
            <el-option label="全部来源" value="all"></el-option>
            <el-option label="VIP" value="vip"></el-option>
            <el-option label="VIP2" value="vip2"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="用户类型">
          <el-select v-model="statusFilter" placeholder="全部类型" @change="handleFilterChange">
            <el-option label="全部类型" value="all"></el-option>
            <el-option label="普通 VIP" value="1"></el-option>
            <el-option label="校长用户" value="2"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
    </div>

    <div class="panel check-panel">
      <div class="panel-header">
        <span>加盟范围校验</span>
        <span class="panel-subtitle">输入新校区地址，2 公里内已有校区则不可加盟</span>
      </div>
      <div class="check-toolbar">
        <el-input
          v-model.trim="campusCheckForm.address"
          placeholder="请输入新校区的详细地址"
          clearable
          size="small"
          @keyup.enter.native="runCampusCheck">
        </el-input>
        <el-button
          size="small"
          type="primary"
          :loading="campusCheckLoading"
          @click="runCampusCheck">
          开始校验
        </el-button>
      </div>
      <div v-if="campusCheckResult" class="check-result">
        <div class="check-status" :class="campusCheckResult.allowed ? 'allow' : 'blocked'">
          {{ campusCheckResult.allowed ? '该地址 2 公里内没有现有校区，可以继续加盟评估。' : '该地址 2 公里内已有校区，不能加盟。' }}
        </div>
        <div class="check-meta">
          <span>校验地址：{{ campusCheckResult.address }}</span>
          <span>解析坐标：{{ campusCheckResult.targetLocation.latitude }}, {{ campusCheckResult.targetLocation.longitude }}</span>
          <span>范围内校区：{{ campusCheckResult.nearbyCount }} 个</span>
        </div>
        <el-table
          v-if="campusCheckResult.nearbySchools && campusCheckResult.nearbySchools.length"
          size="small"
          :data="campusCheckResult.nearbySchools"
          border
          max-height="220">
          <el-table-column prop="campusName" label="校区" min-width="140" show-overflow-tooltip></el-table-column>
          <el-table-column prop="sourceLabel" label="来源" width="80"></el-table-column>
          <el-table-column prop="statusLabel" label="类型" width="90"></el-table-column>
          <el-table-column prop="distance" label="距离(米)" width="100"></el-table-column>
          <el-table-column prop="mobile" label="手机号" width="120"></el-table-column>
          <el-table-column prop="address" label="地址" min-width="180" show-overflow-tooltip></el-table-column>
        </el-table>
      </div>
    </div>

    <div class="content-grid">
      <div class="left-column">
        <div class="panel map-panel">
          <div class="panel-header">
            <span>学校地图</span>
            <span class="panel-subtitle">当前展示 {{ filteredSchools.length }} 个学校点位</span>
          </div>
          <div v-if="mapError" class="map-empty error-text">{{ mapError }}</div>
          <div v-else-if="!filteredSchools.length" class="map-empty">暂无符合条件的学校位置数据</div>
          <div v-else ref="schoolMap" class="school-map"></div>
        </div>

        <div class="panel detail-panel">
          <div class="panel-header">
            <span>学校详情</span>
          </div>
          <div v-if="activeSchool" class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">校区名称</span>
              <span class="detail-value">{{ activeSchool.campusName || '未填写' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">用户昵称</span>
              <span class="detail-value">{{ activeSchool.name || '未填写' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">手机号</span>
              <span class="detail-value">{{ activeSchool.mobile || '未填写' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">数据来源</span>
              <span class="detail-value">{{ activeSchool.sourceLabel }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">用户类型</span>
              <span class="detail-value">{{ activeSchool.statusLabel }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">经纬度</span>
              <span class="detail-value">{{ activeSchool.latitude }}, {{ activeSchool.longitude }}</span>
            </div>
            <div class="detail-item full">
              <span class="detail-label">详细地址</span>
              <span class="detail-value">{{ activeSchool.address || '未填写' }}</span>
            </div>
          </div>
          <div v-else class="detail-empty">点击地图点位或右侧列表，可查看学校具体位置。</div>
        </div>
      </div>

      <div class="right-column">
        <div class="panel chart-panel">
          <div class="panel-header">
            <span>数据看板</span>
          </div>
          <div ref="sourceChart" class="chart-box"></div>
        </div>

        <div class="panel list-panel">
          <div class="panel-header">
            <span>学校列表</span>
            <span class="panel-subtitle">支持点击定位</span>
          </div>
          <el-table
            size="small"
            :data="filteredSchools"
            v-loading="loading"
            border
            height="520"
            highlight-current-row
            :row-class-name="tableRowClassName">
            <el-table-column prop="campusName" label="学校" min-width="140" show-overflow-tooltip></el-table-column>
            <el-table-column prop="sourceLabel" label="来源" width="80"></el-table-column>
            <el-table-column prop="statusLabel" label="类型" width="90"></el-table-column>
            <el-table-column prop="mobile" label="手机号" width="120"></el-table-column>
            <el-table-column prop="address" label="地址" min-width="180" show-overflow-tooltip></el-table-column>
            <el-table-column label="操作" width="90" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" @click="focusSchool(scope.row)">定位</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <div class="panel missing-panel" v-if="missingSchools.length">
      <div class="panel-header">
        <span>未定位学校</span>
        <span class="panel-subtitle">共 {{ missingSchools.length }} 个，需补充地图位置后才能在地图展示</span>
      </div>
      <el-table size="small" :data="missingSchools" border max-height="260">
        <el-table-column prop="campusName" label="学校" min-width="160" show-overflow-tooltip></el-table-column>
        <el-table-column prop="sourceLabel" label="来源" width="80"></el-table-column>
        <el-table-column prop="statusLabel" label="类型" width="90"></el-table-column>
        <el-table-column prop="mobile" label="手机号" width="120"></el-table-column>
        <el-table-column prop="address" label="地址" min-width="220" show-overflow-tooltip></el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { checkCampusFranchise, getUserList, getUserList2 } from "../../api/userMG";

export default {
  name: "SchoolMap",
  data() {
    return {
      loading: false,
      keyword: "",
      sourceFilter: "all",
      statusFilter: "all",
      allSchools: [],
      activeSchoolId: "",
      mapError: "",
      mapInstance: null,
      tileLayer: null,
      markerLayer: null,
      candidateMarker: null,
      schoolMarkers: {},
      sourceChart: null,
      resizeHandler: null,
      campusCheckLoading: false,
      campusCheckForm: {
        address: ""
      },
      campusCheckResult: null,
      summary: {
        total: 0,
        located: 0,
        vip: 0,
        vipPrincipal: 0,
        vip2: 0,
        vip2Principal: 0
      }
    };
  },
  computed: {
    filteredSchools() {
      var keyword = (this.keyword || "").toLowerCase();
      return this.allSchools.filter((item) => {
        if (!item.hasLocation) {
          return false;
        }
        if (this.sourceFilter !== "all" && item.source !== this.sourceFilter) {
          return false;
        }
        if (this.statusFilter !== "all" && String(item.status) !== this.statusFilter) {
          return false;
        }
        if (!keyword) {
          return true;
        }
        var text = [
          item.campusName,
          item.name,
          item.mobile,
          item.address
        ].join(" ").toLowerCase();
        return text.indexOf(keyword) > -1;
      });
    },
    missingSchools() {
      return this.allSchools.filter((item) => !item.hasLocation);
    },
    activeSchool() {
      var school = null;
      for (var i = 0; i < this.filteredSchools.length; i++) {
        if (this.filteredSchools[i].id === this.activeSchoolId) {
          school = this.filteredSchools[i];
          break;
        }
      }
      if (!school && this.filteredSchools.length) {
        school = this.filteredSchools[0];
      }
      return school;
    }
  },
  mounted() {
    this.initDashboard();
    this.resizeHandler = this.handleResize.bind(this);
    window.addEventListener("resize", this.resizeHandler);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeHandler);
    if (this.sourceChart) {
      this.sourceChart.dispose();
      this.sourceChart = null;
    }
    if (this.mapInstance) {
      this.mapInstance.remove();
      this.mapInstance = null;
    }
  },
  methods: {
    async initDashboard() {
      this.loading = true;
      this.mapError = "";
      try {
        await this.ensureLeaflet();
        await this.loadSchoolData();
        this.updateSummary();
        this.$nextTick(() => {
          this.initMap();
          this.renderChart();
          this.handleFilterChange();
        });
      } catch (error) {
        this.mapError = "地图资源加载失败，请检查网络后重试";
        this.$message.error(this.mapError);
      } finally {
        this.loading = false;
      }
    },
    async loadSchoolData() {
      var results = await Promise.all([
        this.fetchSchoolsBySource({
          apiMethod: getUserList,
          source: "vip",
          sourceLabel: "VIP",
          statusField: "isVip",
          campusField: "campusName",
          addressField: "address",
          latitudeField: "latitude",
          longitudeField: "longitude"
        }),
        this.fetchSchoolsBySource({
          apiMethod: getUserList2,
          source: "vip2",
          sourceLabel: "VIP2",
          statusField: "isVip2",
          campusField: "campusName2",
          addressField: "address2",
          latitudeField: "latitude2",
          longitudeField: "longitude2"
        })
      ]);

      this.allSchools = results[0].concat(results[1]);
      if (this.activeSchool && this.activeSchool.id) {
        this.activeSchoolId = this.activeSchool.id;
      } else if (this.filteredSchools.length) {
        this.activeSchoolId = this.filteredSchools[0].id;
      } else {
        this.activeSchoolId = "";
      }
    },
    async fetchSchoolsBySource(config) {
      var schools = [];
      var statuses = [1, 2];
      for (var i = 0; i < statuses.length; i++) {
        var status = statuses[i];
        var list = await this.fetchPagedUsers(config.apiMethod, config.statusField, status);
        for (var j = 0; j < list.length; j++) {
          schools.push(this.normalizeSchool(list[j], config));
        }
      }
      return schools;
    },
    async fetchPagedUsers(apiMethod, statusField, statusValue) {
      var pageSize = 20;
      var firstParams = {
        mobile: "",
        page: 0
      };
      firstParams[statusField] = statusValue;

      var firstRes = await apiMethod(firstParams);
      if (!firstRes || firstRes.code !== 200) {
        throw new Error(firstRes && firstRes.msg ? firstRes.msg : "获取学校数据失败");
      }
      var firstData = firstRes && firstRes.data ? firstRes.data : {};
      var list = firstData.list || [];
      var total = Number(firstData.count || list.length || 0);
      var requests = [];

      for (var offset = pageSize; offset < total; offset += pageSize) {
        var nextParams = {
          mobile: "",
          page: offset
        };
        nextParams[statusField] = statusValue;
        requests.push(apiMethod(nextParams));
      }

      if (!requests.length) {
        return list;
      }

      var rest = await Promise.all(requests);
      for (var i = 0; i < rest.length; i++) {
        if (!rest[i] || rest[i].code !== 200) {
          throw new Error(rest[i] && rest[i].msg ? rest[i].msg : "获取学校数据失败");
        }
        var current = rest[i] && rest[i].data && rest[i].data.list ? rest[i].data.list : [];
        list = list.concat(current);
      }
      return list;
    },
    normalizeSchool(item, config) {
      var latitude = this.parseCoordinate(item[config.latitudeField]);
      var longitude = this.parseCoordinate(item[config.longitudeField]);
      var status = Number(item[config.statusField] || 0);
      var hasLocation = latitude !== null && longitude !== null;

      return {
        id: config.source + "-" + item.id + "-" + status,
        userId: item.id,
        source: config.source,
        sourceLabel: config.sourceLabel,
        status: status,
        statusLabel: status === 2 ? "校长用户" : config.sourceLabel + "用户",
        campusName: item[config.campusField] || "未命名学校",
        address: item[config.addressField] || "",
        latitude: latitude,
        longitude: longitude,
        hasLocation: hasLocation,
        name: item.name || "",
        mobile: item.mobile || "",
        avatar: item.avatar || ""
      };
    },
    parseCoordinate(value) {
      if (value === "" || value === null || typeof value === "undefined") {
        return null;
      }
      var number = Number(value);
      return isNaN(number) ? null : number;
    },
    updateSummary() {
      var summary = {
        total: this.allSchools.length,
        located: 0,
        vip: 0,
        vipPrincipal: 0,
        vip2: 0,
        vip2Principal: 0
      };

      for (var i = 0; i < this.allSchools.length; i++) {
        var item = this.allSchools[i];
        if (item.hasLocation) {
          summary.located += 1;
        }
        if (item.source === "vip" && item.status === 1) {
          summary.vip += 1;
        }
        if (item.source === "vip" && item.status === 2) {
          summary.vipPrincipal += 1;
        }
        if (item.source === "vip2" && item.status === 1) {
          summary.vip2 += 1;
        }
        if (item.source === "vip2" && item.status === 2) {
          summary.vip2Principal += 1;
        }
      }

      this.summary = summary;
    },
    async ensureLeaflet() {
      if (window.L) {
        return;
      }
      this.appendStyle(
        "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css",
        "leaflet-style"
      );
      await this.appendScript(
        "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js",
        "leaflet-script"
      );
    },
    appendStyle(href, id) {
      if (document.getElementById(id)) {
        return;
      }
      var link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    },
    appendScript(src, id) {
      return new Promise((resolve, reject) => {
        if (document.getElementById(id)) {
          if (window.L) {
            resolve();
            return;
          }
        }
        var script = document.createElement("script");
        script.id = id;
        script.src = src;
        script.onload = function() {
          resolve();
        };
        script.onerror = function() {
          reject(new Error("load script error"));
        };
        document.body.appendChild(script);
      });
    },
    initMap() {
      if (!this.filteredSchools.length || !window.L || !this.$refs.schoolMap) {
        this.renderCandidateMarker();
        return;
      }
      var L = window.L;
      if (!this.mapInstance) {
        this.mapInstance = L.map(this.$refs.schoolMap, {
          zoomControl: true,
          minZoom: 4
        });
        this.initTileLayer(L);
        this.markerLayer = L.layerGroup().addTo(this.mapInstance);
      }
      this.renderMarkers();
    },
    initTileLayer(L) {
      var providers = [
        {
          name: "高德地图",
          url: "https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=8&x={x}&y={y}&z={z}",
          options: {
            subdomains: ["1", "2", "3", "4"],
            maxZoom: 18,
            attribution: "&copy; 高德地图"
          }
        },
        {
          name: "OpenStreetMap",
          url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          options: {
            maxZoom: 19,
            attribution: "&copy; OpenStreetMap"
          }
        }
      ];
      var self = this;

      var mountProvider = function(index) {
        var provider = providers[index];
        var tileErrorCount = 0;

        if (!provider || !self.mapInstance) {
          return;
        }
        if (self.tileLayer) {
          self.mapInstance.removeLayer(self.tileLayer);
          self.tileLayer = null;
        }

        self.tileLayer = L.tileLayer(provider.url, provider.options);
        self.tileLayer.on("tileerror", function() {
          tileErrorCount += 1;
          if (tileErrorCount >= 3 && index < providers.length - 1) {
            mountProvider(index + 1);
          }
        });
        self.tileLayer.addTo(self.mapInstance);
      };

      mountProvider(0);
    },
    renderMarkers() {
      if (!this.mapInstance || !this.markerLayer || !window.L) {
        return;
      }
      var L = window.L;
      this.markerLayer.clearLayers();
      this.schoolMarkers = {};
      var bounds = [];

      for (var i = 0; i < this.filteredSchools.length; i++) {
        let school = this.filteredSchools[i];
        var marker = L.marker([school.latitude, school.longitude], {
          icon: L.divIcon({
            className: "school-marker-wrap",
            html: '<span class="school-marker ' + this.getMarkerClass(school) + '"></span>',
            iconSize: [20, 20],
            iconAnchor: [10, 10]
          })
        });
        marker.bindPopup(this.getPopupHtml(school));
        marker.on("click", () => {
          this.activeSchoolId = school.id;
        });
        marker.addTo(this.markerLayer);
        this.schoolMarkers[school.id] = marker;
        bounds.push([school.latitude, school.longitude]);
      }

      if (bounds.length === 1) {
        this.mapInstance.setView(bounds[0], 13);
      } else if (bounds.length > 1) {
        this.mapInstance.fitBounds(bounds, {
          padding: [30, 30]
        });
      }

      if (this.activeSchool) {
        this.focusSchool(this.activeSchool, false);
      }
      this.renderCandidateMarker();
    },
    getPopupHtml(school) {
      return (
        '<div class="popup-card">' +
        '<div class="popup-title">' + school.campusName + "</div>" +
        '<div>来源：' + school.sourceLabel + "</div>" +
        '<div>类型：' + school.statusLabel + "</div>" +
        '<div>手机号：' + (school.mobile || "未填写") + "</div>" +
        '<div>地址：' + (school.address || "未填写") + "</div>" +
        "</div>"
      );
    },
    getMarkerClass(school) {
      if (school.source === "vip" && school.status === 2) {
        return "marker-principal";
      }
      if (school.source === "vip2" && school.status === 2) {
        return "marker-principal-2";
      }
      if (school.source === "vip2") {
        return "marker-vip2";
      }
      return "marker-vip";
    },
    focusSchool(school, openPopup) {
      if (!school || !this.mapInstance) {
        return;
      }
      this.activeSchoolId = school.id;
      var marker = this.schoolMarkers[school.id];
      if (marker) {
        this.mapInstance.setView([school.latitude, school.longitude], 15);
        if (openPopup !== false) {
          marker.openPopup();
        }
      }
    },
    handleFilterChange() {
      if (!this.filteredSchools.length) {
        this.activeSchoolId = "";
      } else if (!this.activeSchool || !this.activeSchool.id) {
        this.activeSchoolId = this.filteredSchools[0].id;
      }
      this.$nextTick(() => {
        this.renderMarkers();
        this.renderChart();
      });
    },
    async runCampusCheck() {
      if (!this.campusCheckForm.address) {
        this.$message.warning("请输入新校区的详细地址");
        return;
      }
      this.campusCheckLoading = true;
      try {
        var res = await checkCampusFranchise({
          address: this.campusCheckForm.address,
          radius: 2000
        });
        if (!res || res.code !== 200) {
          this.$message.error(res && res.msg ? res.msg : "校验失败，请稍后重试");
          return;
        }
        this.campusCheckResult = res.data || null;
        this.$nextTick(() => {
          this.renderCandidateMarker();
          this.focusCampusCheckResult();
        });
      } finally {
        this.campusCheckLoading = false;
      }
    },
    renderCandidateMarker() {
      if (!this.mapInstance || !window.L) {
        return;
      }
      if (this.candidateMarker) {
        this.mapInstance.removeLayer(this.candidateMarker);
        this.candidateMarker = null;
      }
      if (!this.campusCheckResult || !this.campusCheckResult.targetLocation) {
        return;
      }
      var L = window.L;
      var targetLocation = this.campusCheckResult.targetLocation;
      this.candidateMarker = L.circleMarker([targetLocation.latitude, targetLocation.longitude], {
        radius: 10,
        color: "#ef4444",
        fillColor: "#ef4444",
        fillOpacity: 0.35,
        weight: 3
      }).addTo(this.mapInstance);
      this.candidateMarker.bindPopup(
        '<div class="popup-card">' +
        '<div class="popup-title">待加盟新校区</div>' +
        '<div>地址：' + this.campusCheckResult.address + "</div>" +
        '<div>结论：' + (this.campusCheckResult.allowed ? "可继续评估" : "2公里内不可加盟") + "</div>" +
        "</div>"
      );
    },
    focusCampusCheckResult() {
      if (!this.mapInstance || !this.campusCheckResult || !this.campusCheckResult.targetLocation) {
        return;
      }
      var targetLocation = this.campusCheckResult.targetLocation;
      var bounds = [[targetLocation.latitude, targetLocation.longitude]];
      var nearbySchools = this.campusCheckResult.nearbySchools || [];
      for (var i = 0; i < nearbySchools.length; i++) {
        bounds.push([nearbySchools[i].latitude, nearbySchools[i].longitude]);
      }
      if (bounds.length === 1) {
        this.mapInstance.setView(bounds[0], 14);
      } else {
        this.mapInstance.fitBounds(bounds, {
          padding: [40, 40]
        });
      }
      if (this.candidateMarker) {
        this.candidateMarker.openPopup();
      }
    },
    renderChart() {
      if (!this.$refs.sourceChart) {
        return;
      }
      if (!this.sourceChart) {
        this.sourceChart = this.$echarts.init(this.$refs.sourceChart);
      }
      this.sourceChart.setOption({
        tooltip: {
          trigger: "axis"
        },
        grid: {
          left: 30,
          right: 20,
          top: 30,
          bottom: 20,
          containLabel: true
        },
        xAxis: {
          type: "category",
          data: ["VIP", "VIP校长", "VIP2", "VIP2校长"],
          axisLine: {
            lineStyle: {
              color: "#cfd8e3"
            }
          },
          axisLabel: {
            color: "#5b6472"
          }
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          axisLine: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: "#eef2f7"
            }
          },
          axisLabel: {
            color: "#5b6472"
          }
        },
        series: [
          {
            name: "学校数",
            type: "bar",
            barWidth: 28,
            data: [
              this.summary.vip,
              this.summary.vipPrincipal,
              this.summary.vip2,
              this.summary.vip2Principal
            ],
            itemStyle: {
              color: function(params) {
                var colors = ["#3b82f6", "#0f766e", "#8b5cf6", "#c2410c"];
                return colors[params.dataIndex];
              },
              borderRadius: [8, 8, 0, 0]
            }
          }
        ]
      });
    },
    handleResize() {
      if (this.sourceChart) {
        this.sourceChart.resize();
      }
      if (this.mapInstance) {
        this.mapInstance.invalidateSize();
      }
    },
    tableRowClassName({ row }) {
      return row.id === this.activeSchoolId ? "current-row" : "";
    }
  }
};
</script>

<style scoped>
.school-map-page {
  color: #243042;
}

.top-bar {
  margin: 18px 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-intro h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.page-intro p {
  margin: 8px 0 0;
  color: #6b7280;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.summary-card,
.panel {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.summary-card {
  padding: 18px 20px;
}

.summary-label {
  color: #6b7280;
  font-size: 13px;
}

.summary-value {
  margin-top: 10px;
  font-size: 30px;
  line-height: 1;
  font-weight: 700;
  color: #111827;
}

.filters-panel {
  padding: 18px 20px 2px;
  margin-bottom: 16px;
}

.check-panel {
  margin-bottom: 16px;
}

.check-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
}

.check-result {
  margin-top: 16px;
}

.check-status {
  padding: 12px 14px;
  border-radius: 12px;
  font-weight: 600;
}

.check-status.allow {
  color: #166534;
  background: #dcfce7;
}

.check-status.blocked {
  color: #991b1b;
  background: #fee2e2;
}

.check-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin: 12px 0 14px;
  color: #475569;
  font-size: 13px;
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(380px, 0.9fr);
  gap: 16px;
}

.left-column,
.right-column {
  min-width: 0;
}

.panel {
  padding: 18px 20px;
}

.panel + .panel {
  margin-top: 16px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
}

.panel-subtitle {
  color: #94a3b8;
  font-size: 12px;
  font-weight: 400;
}

.school-map {
  height: 520px;
  border-radius: 14px;
  overflow: hidden;
}

.map-empty {
  height: 520px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  background: linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
  border-radius: 14px;
}

.error-text {
  color: #ef4444;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 18px;
}

.detail-item {
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fafc;
}

.detail-item.full {
  grid-column: 1 / -1;
}

.detail-label {
  display: block;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 6px;
}

.detail-value {
  color: #0f172a;
  word-break: break-all;
}

.detail-empty {
  color: #94a3b8;
  padding: 20px 0;
}

.chart-box {
  height: 260px;
}

.missing-panel {
  margin-top: 16px;
}

.list-panel >>> .current-row > td {
  background: rgba(59, 130, 246, 0.08) !important;
}

.school-map-page >>> .school-marker-wrap {
  background: transparent;
  border: none;
}

.school-map-page >>> .school-marker {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.08), 0 4px 10px rgba(15, 23, 42, 0.18);
}

.school-map-page >>> .marker-vip {
  background: #3b82f6;
}

.school-map-page >>> .marker-principal {
  background: #0f766e;
}

.school-map-page >>> .marker-vip2 {
  background: #8b5cf6;
}

.school-map-page >>> .marker-principal-2 {
  background: #c2410c;
}

.school-map-page >>> .popup-card {
  line-height: 1.7;
  color: #334155;
}

.school-map-page >>> .popup-title {
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

@media (max-width: 1600px) {
  .summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/user/SchoolMap.vue